import { useErrorText, SmartSelect, nameKeywords } from "@positive/ui";
import { Button, CheckBox, Dialog, Label, MessageStrip } from "@ui5/webcomponents-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useIdentity } from "./IdentityProvider";
import { localName, type SessionContext } from "./session";

export interface BranchSwitchDialogProps {
  open: boolean;
  session: SessionContext;
  /** At login with several branches and none selected (P12): no Cancel, the dialog cannot be dismissed. */
  required?: boolean;
  onClose: () => void;
}

/**
 * Company / branch picker (P12, ADR-0003/D-6). The choice is stored server-side for the Keycloak session through
 * `POST /api/platform/session/context` — never a browser or PC setting. Lists contain only what the user's data
 * scopes allow. "All my companies" widens lists to every company of the data scope (ADR-0003 note 12); new records
 * always belong to the session company.
 */
export function BranchSwitchDialog({ open, session, required = false, onClose }: BranchSwitchDialogProps) {
  const { t, i18n } = useTranslation("shell");
  const describeError = useErrorText();
  const { switchContext } = useIdentity();
  const initialCompany = session.session.companyId ?? session.companies[0]?.id ?? "";
  const [companyId, setCompanyId] = useState(initialCompany);
  const branchesOf = (company: string) => session.branches.filter((branch) => branch.companyId === company);
  const [branchId, setBranchId] = useState(session.session.branchId ?? branchesOf(initialCompany)[0]?.id ?? "");
  const [allCompanies, setAllCompanies] = useState(session.session.allCompanies === true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const branches = branchesOf(companyId);
  const unchanged =
    companyId === session.session.companyId &&
    branchId === session.session.branchId &&
    allCompanies === (session.session.allCompanies === true);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      await switchContext({ companyId, branchId, allCompanies });
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      headerText={required ? t("context.selectTitle") : t("context.switchTitle")}
      data-testid="branch-dialog"
      onBeforeClose={(event) => {
        if (required) event.preventDefault();
      }}
      onClose={onClose}
      footer={
        <div className="positive-dialog-footer">
          <Button
            design="Emphasized"
            disabled={busy || branchId === "" || (!required && unchanged)}
            onClick={() => void apply()}
            data-testid="branch-apply"
          >
            {t("context.apply")}
          </Button>
          {!required && (
            <Button design="Transparent" onClick={onClose} data-testid="branch-cancel">
              {t("context.cancel")}
            </Button>
          )}
        </div>
      }
    >
      <div className="positive-form">
        {required && <MessageStrip hideCloseButton>{t("context.selectHint")}</MessageStrip>}
        <Label for="context-company" required>
          {t("context.company")}
        </Label>
        <SmartSelect
          id="context-company"
          value={companyId}
          data-testid="branch-company"
          options={session.companies.map((company) => ({
            value: company.id,
            label: localName(company, i18n.language),
            keywords: nameKeywords(company),
          }))}
          onChange={(picked) => {
            const next = picked;
            setCompanyId(next);
            setBranchId(branchesOf(next)[0]?.id ?? "");
          }}
        />
        <Label for="context-branch" required>
          {t("context.branch")}
        </Label>
        <SmartSelect
          id="context-branch"
          value={branchId}
          data-testid="branch-branch"
          options={branches.map((branch) => ({
            value: branch.id,
            label: localName(branch, i18n.language),
            keywords: nameKeywords(branch),
          }))}
          onChange={(picked) => {
            setBranchId(picked);
          }}
        />
        {session.companies.length > 1 && (
          <>
            <CheckBox
              text={t("context.allCompanies")}
              checked={allCompanies}
              data-testid="branch-all-companies"
              onChange={() => {
                setAllCompanies((current) => !current);
              }}
            />
            <MessageStrip hideCloseButton design="Information">
              {t("context.allCompaniesHint")}
            </MessageStrip>
          </>
        )}
        {error !== null && (
          <MessageStrip design="Negative" hideCloseButton data-testid="branch-error">
            {describeError(error).message}
          </MessageStrip>
        )}
      </div>
    </Dialog>
  );
}
